import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { NamedPersonSearchState, searchNamedPerson, searchPhoneNumber, clearSearch, clearAllSearches, NumberSearchState, ContactSearchState } from "./search-slice";
import { updateHistoryFilter } from "../../../../lib/redux/filters/slice";
import { AppDispatch } from "../../../../store";
import { FilterOperator } from "../../../../interfaces/sessions";
import { BackendTables, OBJECTION_COLUMNS } from "../../../../interfaces/db";
import { CallMetricColumns, ReviewStatusColumns, SESSION_METRICS_COLUMNS, SessionV2Columns } from "../../../../interfaces/table";
import { useCurrentStageConfig } from "../../../../lib/redux/analytics/stage-config";
import { LIVE_DURATION_GT_ZERO } from "../../../../cfg/analytics";

// Selector to get search results for a specific query
export const selectPersonSearchResults = (state: RootState, query: string): NamedPersonSearchState | undefined => {
  return state.namedPersonSearch.searches[query]
};

export const selectNumberSearchResults = (state: RootState, query: string): NumberSearchState | undefined => {
  return state.namedPersonSearch.phoneNumberSearches[query]
};

export const selectContactForPlatformPersonId = (state: RootState, platformPersonId: string): ContactSearchState  | undefined => {
  return state.namedPersonSearch.contactSearches[platformPersonId]
};

// Hook for using the named person search functionality
export const useQuickSearches = () => {
  const dispatch = useDispatch<AppDispatch>();
  const stageConfig = useCurrentStageConfig();

  // Helper function to dispatch to the appropriate filter slice
  const dispatchFilterUpdate = useCallback((filterValue: any) => {
    dispatch(updateHistoryFilter(filterValue));
  }, [dispatch]);

  const search = useCallback((query: string) => {
    // Check if query contains digits
    const hasDigits = /\d/.test(query);
    
    if (hasDigits && query.length >= 4) {
      // If query has 4 or more digits, treat as phone search
      dispatch(searchPhoneNumber(query));
    } else {
      // Otherwise treat as name search
      dispatch(searchNamedPerson(query));
    }
  }, [dispatch]);

  const searchForPhones = useCallback((phoneNumbers: string[]) => {
    // Clear existing filters and set phone filter
    dispatchFilterUpdate([[{
      table: BackendTables.SESSIONS_V2,
      column_name: SessionV2Columns.CUSTOMER_ADDRESS, // Changed from AGENT_ADDRESS to CUSTOMER_ADDRESS
      value_safe: phoneNumbers.map(phoneNumber => phoneNumber.replaceAll(/[^0-9]/g, "")),
      operator: FilterOperator.IN
    }]]);
  }, [dispatchFilterUpdate]);

  const searchForName = useCallback((name: string) => {
    // Clear existing filters and set name filter
    dispatchFilterUpdate([[{
      table: BackendTables.SESSION_METRICS,
      column_name: SESSION_METRICS_COLUMNS.PROSPECT_NAME,
      value_safe: [name],
      operator: FilterOperator.IN
    }]]);
  }, [dispatchFilterUpdate]);

  const searchForConnects = useCallback(() => {
    // Try to find the Connect stage from stage definitions
    const connectStage = stageConfig?.options?.find(stage => 
      stage.label.toLowerCase().includes('connect') || 
      stage.label.toLowerCase().includes('answer')
    );

    // Use stage definition filter if available, otherwise fallback to default filter
    const connectFilter = connectStage?.filter || [[LIVE_DURATION_GT_ZERO]];
    dispatchFilterUpdate(connectFilter);
  }, [dispatchFilterUpdate, stageConfig]);

  const searchForObjections = useCallback(() => {
    // Get all objection columns and create a filter that checks for live connect AND any objection
    const objectionColumns = Object.values(OBJECTION_COLUMNS);
    
    dispatchFilterUpdate([
      [LIVE_DURATION_GT_ZERO],
      [...objectionColumns.map(column => ({
        column_name: column,
        table: BackendTables.SESSION_METRICS,
        operator: FilterOperator.GT,
        value_safe: 0
      }))],
    ]);
  }, [dispatchFilterUpdate]);


  const searchForReviews = useCallback(() => {
    // Get all objection columns and create a filter that checks for live connect AND any objection
    dispatchFilterUpdate([[{
      column_name: ReviewStatusColumns.IS_OPEN,
      table: BackendTables.REVIEW_STATUSES,
      operator: FilterOperator.EQ,
      value_safe: true
    }]]);
  }, [dispatchFilterUpdate]);

  const clearSearchResults = useCallback((query: string) => {
    dispatch(clearSearch({ query }));
  }, [dispatch]);

  const clearAllSearchResults = useCallback(() => {
    dispatch(clearAllSearches());
  }, [dispatch]);

  const clearAllFilters = useCallback(() => {
    dispatchFilterUpdate([]);
  }, [dispatchFilterUpdate]);

  return {
    search,
    searchForPhones,
    searchForName,
    searchForConnects,
    searchForObjections,
    searchForReviews,
    clearSearchResults,
    clearAllSearchResults,
    clearAllFilters,
  };
}; 